import { useFields } from './FieldsContext';
import { fieldLabelText } from '@repo/lib';
import { elementFieldTypeEnums } from '@dashboard/services/pageFieldTypes';
import { toast } from '@dashboard/context/ToastContext';
import logException from '@dashboard/services/logException';
import { confirm } from '@dashboard/lib/confirm';
import { useFieldEdit } from './FieldEditContext';
import { useFormLayout } from './FormLayoutContext';
import { useFieldSource } from './FieldSourceContext';

export function useFieldHelpers(fieldId) {
  if (!fieldId) {
    throw new Error('Field ID is required');
  }

  const {
    saveField,
    deleteField,
    refresh,
    isLocked,
    isUndeletable = isLocked,
    noun,
  } = useFieldSource();

  const { state: fields, dispatch } = useFields();
  const { setIsAddingField, hasUnsavedChanges, setHasUnsavedChanges } = useFieldEdit();
  const formLayout = useFormLayout();

  const field = fields.find(field => field._id === fieldId || field.key === fieldId);

  async function onSaveField(values) {
    if (
      values.element !== elementFieldTypeEnums.SELECT &&
      values.element !== elementFieldTypeEnums.MULTI_SELECT &&
      values.element !== elementFieldTypeEnums.MULTIPLE_CHOICE
    ) {
      delete values.options;
    }

    if (values.element === 'tax-id') {
      values.type = 'tax-id';
    }

    if (values.element === 'po-number') {
      values.type = 'po-number';
    }

    const groupId = !field._id && field.key ? formLayout?.draftGroupFor(field.key) : null;
    if (groupId) values.groupId = groupId;

    const { data, error } = await saveField(values, field._id);

    if (error) {
      return { error };
    }

    if (groupId && data?._id) formLayout.placeSavedField(data._id, groupId);

    if (!field._id) {
      toast({
        title: noun === 'question' ? 'Question added' : 'Field added',
        description: fieldLabelText(data?.label ?? values.label),
        type: 'success',
      });
    }

    dispatch({
      type: 'update',
      item: field,
      values: {
        ...data,
        isEditing: true,
      },
    });

    setHasUnsavedChanges(false);
    setIsAddingField(false);

    refresh();

    return { error: '', data };
  }

  async function onRemoveField() {
    if (field.isNew) {
      await onCancelAddField();
      return;
    }

    const del = await confirm(
      noun === 'question' ? 'Delete this question?' : 'Delete this field?',
      {
        confirm: 'Delete',
      },
    );

    if (!del) return;

    try {
      const response = await deleteField(field._id);

      if (response === true) {
        dispatch({ type: 'delete', item: field });

        refresh();
      } else {
        throw new Error();
      }
    } catch (error) {
      logException(error);
      window.alert('Failed to delete field. Try again or contact support');
    }
  }

  // Sends only what the edit endpoint validates plus the flag: it $sets, so the
  // rest of the field is untouched and a half-edited form is never persisted.
  async function onToggleDisabled() {
    if (!field?._id) return;

    const disabled = !field.disabled;

    const { data, error } = await saveField(
      {
        label: field.label,
        element: field.element,
        type: field.type,
        required: field.required,
        disabled,
      },
      field._id,
    );

    if (error || !data) {
      toast({
        title: error?.message || (disabled ? 'Unable to disable field' : 'Unable to enable field'),
        type: 'error',
      });
      return;
    }

    dispatch({ type: 'update', item: field, values: { ...field, ...data } });

    refresh();
  }

  function canRemoveField() {
    if (!field) return;

    if (isUndeletable(field)) return false;

    if (field.element !== 'email') return true;

    const emailFieldsCount = fields.filter(f => f.element === 'email').length;

    return emailFieldsCount > 1;
  }

  async function onCancelAddField() {
    let proceed = true;

    if (hasUnsavedChanges) {
      proceed = await confirm('Discard unsaved changes?', {
        confirm: 'Discard',
      });
    }

    if (!proceed) return;

    dispatch({ type: 'delete', item: field });
    setHasUnsavedChanges(false);
    setIsAddingField(false);
  }

  function onDirty(dirty) {
    if (dirty && field.isEditing) {
      setHasUnsavedChanges(true);
    }

    if (!dirty) {
      setHasUnsavedChanges(false);
    }
  }

  const showRemoveField = canRemoveField();

  return {
    showRemoveField,
    onSaveField,
    onRemoveField,
    onToggleDisabled,
    onCancelAddField,
    onDirty,
  };
}
